import { useState, useCallback } from 'react';
import type { SortingState } from '@tanstack/react-table';
import type { Override } from './api/types';
import { deleteOverride } from './api/client';
import { useOverrides } from './hooks/useOverrides';
import { useCarriers } from './hooks/useCarriers';
import { useAuth } from './hooks/useAuth';
import { OverrideTable } from './components/OverrideTable';
import { FilterBar } from './components/FilterBar';
import { OverrideForm } from './components/OverrideForm';
import { ConfirmModal } from './components/ConfirmModal';
import { AuditLog } from './components/AuditLog';
import { LoginScreen } from './components/LoginScreen';
import { SetupScreen, TokenManagement } from './components/TokenManagement';

const emptyFilters = {
  carrier_code: '',
  fare_family: '',
  correct_shelve: '',
  wrong_shelve: '',
  confirmed: '',
  gds: '',
  booking_code: '',
  search: '',
};

// Map TanStack column IDs to API sort_by values
const sortKeyMap: Record<string, string> = {
  carrierCode: 'carrier_code',
  bookingCode: 'booking_code',
  fareFamily: 'fare_family',
  correctShelve: 'correct_shelve',
  wrongShelve: 'wrong_shelve',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

type ActiveTab = 'overrides' | 'audit' | 'tokens';

export default function App() {
  const { state: authState, role, email, login, logout, setupComplete } = useAuth();

  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOverride, setEditingOverride] = useState<Override | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('overrides');
  const [deleteTarget, setDeleteTarget] = useState<Override | null>(null);

  const sortBy = sorting[0]
    ? sortKeyMap[sorting[0].id] || sorting[0].id
    : 'id';
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  const { data, pagination, loading, reload, removeItem } = useOverrides({
    page,
    per_page: 50,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters,
  });

  const { carriers } = useCarriers();

  const handleEdit = useCallback((override: Override) => {
    setEditingOverride(override);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback((override: Override) => {
    setDeleteTarget(override);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    removeItem(target.id);
    try {
      await deleteOverride(target.id);
      reload();
    } catch (err) {
      console.error('Delete failed:', err);
      reload();
      setErrorMessage(`Failed to delete override #${target.id}`);
      setTimeout(() => setErrorMessage(null), 4000);
    }
  }, [deleteTarget, reload, removeItem]);

  const handleSaved = useCallback(() => {
    setShowForm(false);
    setEditingOverride(null);
    reload();
  }, [reload]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingOverride(null);
  }, []);

  const handleFiltersChange = useCallback((f: typeof filters) => {
    setFilters(f);
    setPage(1);
  }, []);

  // ─── Auth gates ─────────────────────────────────────────
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (authState === 'setup') {
    return <SetupScreen onSetupComplete={setupComplete} />;
  }

  if (authState === 'login') {
    return <LoginScreen onLogin={login} />;
  }

  // ─── Authenticated app ─────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-lg font-semibold text-gray-800">
            Shelve Override Configuration
          </h1>
          <nav className="flex gap-1">
            <button
              onClick={() => setActiveTab('overrides')}
              className={`px-3 py-1 text-sm rounded ${
                activeTab === 'overrides'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overrides
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-3 py-1 text-sm rounded ${
                activeTab === 'audit'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Audit Log
            </button>
            {role === 'user' && (
              <button
                onClick={() => setActiveTab('tokens')}
                className={`px-3 py-1 text-sm rounded ${
                  activeTab === 'tokens'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Tokens
              </button>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {email && (
            <span className="text-xs text-gray-500">{email}</span>
          )}
          {activeTab === 'overrides' && (
            <button
              onClick={() => {
                setEditingOverride(null);
                setShowForm(true);
              }}
              className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              + New Override
            </button>
          )}
          <button
            onClick={logout}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="px-6 py-4">
        {activeTab === 'overrides' && (
          <>
            <FilterBar
              filters={filters}
              onChange={handleFiltersChange}
              carriers={carriers}
            />
            <OverrideTable
              data={data}
              pagination={pagination}
              sorting={sorting}
              onSortingChange={setSorting}
              onPageChange={setPage}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
            />
          </>
        )}
        {activeTab === 'audit' && <AuditLog />}
        {activeTab === 'tokens' && <TokenManagement />}
      </main>

      {errorMessage && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow-lg z-50 text-sm">
          {errorMessage}
        </div>
      )}

      {showForm && (
        <OverrideForm
          carriers={carriers}
          editingOverride={editingOverride}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Override"
          message={`Delete override #${deleteTarget.id} (${deleteTarget.carrierCode} / ${deleteTarget.fareFamily})?`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
